import { default as _log } from '@dw/loglevel';

const logger = _log.getLogger(`dreamdb.replaySocketRequests`);
/**
 * Socket.io's default behavior
 *  - Any event emitted while the Socket is not connected will be buffered until reconnection. So it would replayed
 *    on reconnection.
 *  - Any event emitted while the socket.io connection is broken with server and it's yet not known to the 
 *    socket.io but socket.io is reconnected before known actual disconnection then it would replayed on 
 *    reconnection.
 *  - Any event emitted while the socket.io connection is broken with the server and it's yet not known to the 
 *    socket.io, and after ping timeout socket.io connection is disconnected. then it will not replayed on 
 *    reconnection.
 * 
 * Behavior:
 * - On request, it will put request into queue of pending requests and removed from the queue when it 
 *   successfully proceed.
 * - If socket.io is disconnected then it will not send any requests till `READY `message is received from 
 *   the server, But just put into pending requests queue.
 * - When `READY` message received from the server, it will replay all pending requests.
 */

/**
 * Holds pending requests by couchdb name
 * Format: { $couchdbName: Array of Object{ type, message, callback } } 
 */
let pendingRequests = {};

/**
 * Holds ready status by couchdb name
 * It's value is updated to true, when `READY` message received from server and updated to `false` on client is 
 * disconnected.
 * Format: { $couchdbName: Boolean } 
 */
let ready = {};

/**
 * It's socket's `READY` message handler.
 * It replays all pending requests and remove all pending requests from `pendingRequests`.
 * @param {String} couchdbName 
 * @param {Socket} socket 
 * @param {Function} callback 
 */
const _onReady = (couchdbName, socket, callback) => {
  if(ready[couchdbName]) {
    return;
  }
  
  ready[couchdbName] = true;
  callback({ success: true });
  logger.trace("_onReady: pendingRequests on ready=", pendingRequests);

  if(!pendingRequests[couchdbName] || !pendingRequests[couchdbName].length) {
    return;
  }

  let requests = pendingRequests[couchdbName];
  pendingRequests[couchdbName] = [];

  requests.forEach(request => {
    socket.emit(request.type, request.message, request.callback);
  });

  logger.trace("_onReady: pendingRequests replayed on ready.");
};

/**
 * It's socket's `disconnect` event handler.
 * It sets ready status as `false`.
 * @param {String} couchdbName 
 */
const _onDisconnect = (couchdbName) => {
  ready[couchdbName] = false;
  logger.debug(`_onDisconnect: disconnected. ready=${ready}`);
};

/**
 * It overrides emit function of given socket instance.
 * It adds new request into `pendingRequests` queue before proceding it and after successfully proceed it removes 
 * from `pendingRequests` queue.
 * @param {String} couchdbName 
 * @param {Socket} socket 
 */
export default function(couchdbName, socket) {
  let _emit = socket.emit;
  socket._emit = _emit;

  socket.emit = (function(type, message, callback) {
    let request = {
      type, message, callback
    };

    //If not ready then just put into pending requests
    if(!ready[couchdbName]) {
      (pendingRequests[couchdbName] || (pendingRequests[couchdbName] = [])).push(request);
      logger.trace('emit: status not ready. so, added request into pending requests');
      return;
    }

    (pendingRequests[couchdbName] || (pendingRequests[couchdbName] = [])).push(request);
    logger.trace("emit: new request=", request, "pendingRequests=", pendingRequests);
    _emit.call(this, type, message, (response) => {
      pendingRequests[couchdbName] = pendingRequests[couchdbName].filter(pendingRequest => pendingRequest !== request);
      callback(response);
      logger.trace("emit: request completed. request:", request, "pendingRequests=", pendingRequests);
    });
  }).bind(socket);

  //Bind socket.io's `reconnect` event
  socket.on('READY', (callback) => _onReady(couchdbName, socket, callback));
  socket.on('disconnect', () => _onDisconnect(couchdbName));
}